<template>
    <div class="px-2 py-4 xl:px-10">
        <div class="max-w-8xl mx-auto w-full space-y-4">
            <!-- Page Header -->
            <div class="flex items-center justify-between">
                <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                    Assignments Management
                </h1>
                <button
                    @click="openModal"
                    class="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                    Add Assignment
                </button>
            </div>

            <!-- Search and Filters -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
                <div class="grid grid-cols-1 md:grid-cols-6 gap-4">
                    <!-- Search -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Search
                        </label>
                        <input
                            v-model="searchQuery"
                            @input="debouncedSearch"
                            type="text"
                            placeholder="Search by title..."
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    <!-- Class Subject Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.class_subject_id"
                            :options="classSubjectOptions"
                            label="Class Subject"
                            placeholder="All Class Subjects"
                            :option-label="getClassSubjectLabel"
                            option-value="id"
                            @update:model-value="fetchAssignments"
                        />
                    </div>

                    <!-- Teacher Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.teacher_id"
                            :options="teacherOptions"
                            label="Teacher"
                            placeholder="All Teachers"
                            :option-label="getTeacherName"
                            option-value="id"
                            @update:model-value="fetchAssignments"
                        />
                    </div>

                    <!-- Status Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.status"
                            :options="statusOptions"
                            label="Status"
                            placeholder="All Statuses"
                            option-label="label"
                            option-value="value"
                            @update:model-value="fetchAssignments"
                        />
                    </div>

                    <!-- Class Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.class_id"
                            :options="classOptions"
                            label="Class"
                            placeholder="All Classes"
                            :option-label="getClassName"
                            option-value="id"
                            @update:model-value="fetchAssignments"
                        />
                    </div>

                    <!-- Subject Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.subject_id"
                            :options="subjectOptions"
                            label="Subject"
                            placeholder="All Subjects"
                            :option-label="getSubjectName"
                            option-value="id"
                            @update:model-value="fetchAssignments"
                        />
                    </div>
                </div>
                <div class="flex justify-between items-center mt-4">
                    <button
                        @click="resetFilters()"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    >
                        <RotateCcw class="w-4 h-4 mr-1" />
                        Reset Filters
                    </button>
                </div>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="flex justify-center items-center py-12">
                <div
                    class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
                ></div>
            </div>

            <!-- Error State -->
            <div
                v-if="error"
                class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
            >
                <div class="flex">
                    <div class="flex-shrink-0">
                        <div class="h-5 w-5 text-red-400">⚠️</div>
                    </div>
                    <div class="ml-3">
                        <h3
                            class="text-sm font-medium text-red-800 dark:text-red-200"
                        >
                            Error
                        </h3>
                        <p class="mt-1 text-sm text-red-700 dark:text-red-300">
                            {{ error }}
                        </p>
                        <button
                            @click="clearError"
                            class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-500"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>

            <!-- Data Table -->
            <div
                v-if="!loading && !error"
                class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
            >
                <div
                    class="overflow-x-auto bg-gray-800 dark:bg-gray-900 shadow-md rounded-lg"
                >
                    <table
                        class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                        style="width: 100%"
                    >
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Title
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Class Subject
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Deadline
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Max Score
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Submissions
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Status
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody
                            class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                        >
                            <tr
                                v-for="assignment in assignments"
                                :key="assignment.id"
                                class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div
                                        class="text-sm font-medium text-gray-900 dark:text-white"
                                    >
                                        {{ assignment.title }}
                                    </div>
                                    <div
                                        v-if="assignment.description"
                                        class="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs"
                                        :title="assignment.description"
                                    >
                                        {{ assignment.description }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{
                                        assignment.class_subject?.class
                                            ?.grade_level || "-"
                                    }}
                                    -
                                    {{
                                        assignment.class_subject?.class?.name ||
                                        "-"
                                    }}
                                    -
                                    {{
                                        assignment.class_subject?.subject
                                            ?.name || "-"
                                    }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    <span
                                        :class="{
                                            'text-red-600 dark:text-red-400':
                                                assignment.is_overdue,
                                            'text-yellow-600 dark:text-yellow-400':
                                                !assignment.is_overdue &&
                                                assignment.days_until_deadline !==
                                                    null &&
                                                assignment.days_until_deadline !==
                                                    undefined &&
                                                assignment.days_until_deadline <=
                                                    3,
                                        }"
                                    >
                                        {{ formatDate(assignment.deadline) }}
                                    </span>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ assignment.max_score }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ assignment.submissions_count || 0 }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        v-if="assignment.is_published"
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                    >
                                        Published
                                    </span>
                                    <span
                                        v-else
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                    >
                                        Draft
                                    </span>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                                >
                                    <button
                                        @click="viewAssignment(assignment)"
                                        class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 mr-3"
                                    >
                                        View
                                    </button>
                                    <button
                                        @click="editAssignment(assignment)"
                                        class="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 mr-3"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        @click="viewSubmissions(assignment)"
                                        class="text-purple-600 dark:text-purple-400 hover:text-purple-900 dark:hover:text-purple-300 mr-3"
                                    >
                                        Submissions
                                    </button>
                                    <button
                                        @click="deleteAssignment(assignment)"
                                        class="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div
                    v-if="meta && meta.last_page > 1"
                    class="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6"
                >
                    <div class="flex-1 flex justify-between sm:hidden">
                        <button
                            @click="prevPage"
                            :disabled="meta.current_page === 1"
                            class="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <button
                            @click="nextPage"
                            :disabled="meta.current_page === meta.last_page"
                            class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                    <div
                        class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between"
                    >
                        <div>
                            <p class="text-sm text-gray-700 dark:text-gray-300">
                                Showing
                                <span class="font-medium">{{ meta.from }}</span>
                                to
                                <span class="font-medium">{{ meta.to }}</span>
                                of
                                <span class="font-medium">{{
                                    meta.total
                                }}</span>
                                results
                            </p>
                        </div>
                        <div>
                            <nav
                                class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                                aria-label="Pagination"
                            >
                                <button
                                    @click="prevPage"
                                    :disabled="meta.current_page === 1"
                                    class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <button
                                    @click="nextPage"
                                    :disabled="
                                        meta.current_page === meta.last_page
                                    "
                                    class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import { debounce } from "lodash-es";
import SearchableSelect from "@/components/SearchableSelect.vue";
import { RotateCcw } from "@lucide/vue";
import assignmentApi, { Assignment, AssignmentFilters } from "@/api/assignment";
const router = useRouter();
const assignments = ref<Assignment[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const meta = ref<any>(null);

const searchQuery = ref("");
const filters = ref<AssignmentFilters>({
    status: undefined,
    class_id: undefined,
    subject_id: undefined,
    teacher_id: undefined,
    class_subject_id: undefined,
    per_page: 15,
});

const classSubjectOptions = ref<any[]>([]);
const teacherOptions = ref<any[]>([]);
const classOptions = ref<any[]>([]);
const subjectOptions = ref<any[]>([]);

const statusOptions = [
    { label: "Published", value: "published" },
    { label: "Draft", value: "draft" },
];

const hasActiveFilters = computed(() => {
    return Object.values(filters.value).some(
        (value) => value !== undefined && value !== "",
    );
});

const debouncedSearch = debounce(() => {
    fetchAssignments();
}, 500);

async function fetchAssignments() {
    loading.value = true;
    error.value = null;

    try {
        const response = await assignmentApi.getAll({
            ...filters.value,
            search: searchQuery.value || undefined,
        });

        assignments.value = response.data.items;
        meta.value = response.data.meta;
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to fetch assignments";
    } finally {
        loading.value = false;
    }
}

function resetFilters() {
    filters.value = {
        status: undefined,
        class_id: undefined,
        subject_id: undefined,
        teacher_id: undefined,
        class_subject_id: undefined,
        per_page: 15,
    };
    searchQuery.value = "";
    fetchAssignments();
}

function clearError() {
    error.value = null;
}

function formatDate(dateString: string) {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function getClassSubjectLabel(option: any) {
    return `${option.class?.grade_level} - ${option.class?.name} - ${option.subject?.name}`;
}

function getTeacherName(option: any) {
    return option.user?.name || "-";
}

function getClassName(option: any) {
    return `${option.grade_level} - ${option.name}`;
}

function getSubjectName(option: any) {
    return `${option.code} - ${option.name}`;
}

function openModal() {
    router.push("/assignments/create");
}

function viewAssignment(assignment: Assignment) {
    router.push(`/assignments/${assignment.id}`);
}

function editAssignment(assignment: Assignment) {
    router.push(`/assignments/${assignment.id}/edit`);
}

function viewSubmissions(assignment: Assignment) {
    router.push(`/assignments/${assignment.id}/submissions`);
}

async function deleteAssignment(assignment: Assignment) {
    if (!confirm(`Are you sure you want to delete "${assignment.title}"?`)) {
        return;
    }

    try {
        await assignmentApi.delete(assignment.id);
        await fetchAssignments();
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to delete assignment";
    }
}

function prevPage() {
    if (meta.value && meta.value.current_page > 1) {
        filters.value.page = meta.value.current_page - 1;
        fetchAssignments();
    }
}

function nextPage() {
    if (meta.value && meta.value.current_page < meta.value.last_page) {
        filters.value.page = meta.value.current_page + 1;
        fetchAssignments();
    }
}

onMounted(() => {
    fetchAssignments();
});
</script>
